<template>
  <!-- Removed @dragend to prevent Vue 3 "Extraneous non-emits" warnings on Konva fragments -->
  <v-group ref="rootGroup" :config="{ x, y, draggable: !!draggable }">
    <!-- 1. THE BADGE (Responsive Opacity & Clickable) -->
    <v-group ref="badgeGroup" :config="{ opacity: markerOpacity }">
      <!-- FIELDER BADGE: Professional Pill Shape -->
      <template v-if="type === 'fielder'">
        <!-- NEW: DZR/UZR Zone Indicator (drawn behind the fielder badge) -->
        <v-circle
          v-if="showZone"
          :config="{
            x: 0,
            y: 0,
            radius: activeZoneRadius,
            fill: '#3498db',
            opacity: 0.15,
            stroke: '#2980b9',
            strokeWidth: 1,
            dash: [4, 4],
          }"
        />

        <v-rect
          :config="{
            x: props.showPositionInitials ? -23 : -10,
            y: -10,
            width: props.showPositionInitials ? 46 : 20,
            height: 20,
            cornerRadius: 10,
            fill: '#1565c0',
            stroke: isSelected ? '#FFC107' : 'transparent',
            strokeWidth: isSelected ? 3 : 0,
            shadowColor: 'black',
            shadowBlur: isSelected ? 8 : 4,
            shadowOffset: { x: 1, y: 1 },
            shadowOpacity: 0.4,
            hitStrokeWidth: 16
          }"
        />
        <!-- Position Initials -->
        <v-text
          v-if="props.showPositionInitials"
          :config="{
            text: positionText,
            x: -21,
            y: -5,
            width: 22,
            align: 'center',
            fontSize: 10,
            fontFamily: 'Arial',
            fill: 'white',
            fontStyle: 'bold',
          }"
        />
        <!-- Number Inset -->
        <v-circle
          :config="{ x: props.showPositionInitials ? 12 : 0, y: 0, radius: 8, fill: 'white' }"
        />
        <!-- Uniform Number -->
        <v-text
          :config="{
            text: uniformNumber ? String(uniformNumber) : '-',
            x: props.showPositionInitials ? 4 : -8,
            y: -5,
            width: 16,
            align: 'center',
            fontSize: 10,
            fontFamily: 'Arial',
            fill: '#1565c0',
            fontStyle: 'bold',
          }"
        />
        <!-- NEW: PLAYER NAME LABEL -->
        <v-text
          v-if="playerName"
          :config="{
          text: playerName,
          x: -40,
          y: 16,
          width: 80,
          align: 'center',
          fontSize: 11,
          fontFamily: 'Arial',
          fill: 'black',
          fontStyle: 'bold',
        }"
        />
      </template>

      <!-- RUNNER BADGE: Simple Orange Circle -->
      <template v-else>
        <!-- Add hitStrokeWidth to invisibly expand the touch radius from 24px to 60px! -->
        <v-circle
          :config="{
            radius: 12,
            fill: '#e65100',
            stroke: isSelected ? '#FFC107' : '#ffffff',
            strokeWidth: isSelected ? 4 : 2,
            shadowColor: 'black',
            shadowBlur: isSelected ? 8 : 4,
            shadowOffset: { x: 1, y: 1 },
            shadowOpacity: 0.3,
            hitStrokeWidth: 36,
          }"
        />
        <v-text
          :config="{
            text: uniformNumber ? String(uniformNumber) : '-',
            fontSize: 12,
            fontFamily: 'Arial',
            fill: 'white',
            fontStyle: 'bold',
            align: 'center',
            x: -12,
            y: -6,
            width: 24,
          }"
        />
      </template>


    </v-group>
  </v-group>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useSettingsStore } from 'stores/settings-store'

const props = defineProps<{
  type: 'fielder' | 'runner'
  uniformNumber?: string | number | null
  playerName?: string
  position?: number
  x: number
  y: number
  draggable?: boolean
  isSelected?: boolean
  opacity?: number
  showZone?: boolean
  showPositionInitials?: boolean
  zoneRadius?: number
}>()

const settingsStore = useSettingsStore()

// Explicitly defining dragend stops Vue 3 from trying to automatically inherit it
// as a native DOM event (which causes the Extraneous non-emits warning on Canvas roots)
const emit = defineEmits(['dragend', 'select', 'longpress'])
// --- KONVA EVENT REF WORKAROUNDS ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rootGroup = ref<any>(null)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const badgeGroup = ref<any>(null)

const markerOpacity = computed(() => props.opacity || 1)

// Calculate dynamic UZR zone radius based on fielding position expectations
const activeZoneRadius = computed(() => {
  // Allow manual override from parent component
  if (props.zoneRadius) return props.zoneRadius

  if (props.position) {
    switch (props.position) {
      case 7:
      case 8:
      case 9:
        return 80 // Outfielders cover the most ground
      case 4:
      case 6:
        return 55 // Middle Infielders
      case 3:
      case 5:
        return 40 // Corner Infielders
      case 1:
        return 10
      case 2:
        return 25 // Pitcher and Catcher have very limited zones
    }
  }
  return 45 // Fallback
})

let pressTimer: ReturnType<typeof setTimeout> | null = null

// Manually bind the Konva events to bypass Vue 3's fallthrough attribute warnings
onMounted(() => {
  if (rootGroup.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rootGroup.value.getNode().on('dragend', (e: any) => {
      emit('dragend', e)
    })
    rootGroup.value.getNode().on('dragstart', () => {
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
    })
  }

  if (badgeGroup.value) {
    badgeGroup.value.getNode().on('mousedown touchstart', handlePointerDown)
    badgeGroup.value.getNode().on('mouseup touchend', handlePointerUp)
  }
})

onBeforeUnmount(() => {
  if (rootGroup.value) {
    rootGroup.value.getNode().off('dragend')
    rootGroup.value.getNode().off('dragstart')
  }
  if (badgeGroup.value) {
    badgeGroup.value.getNode().off('mousedown touchstart')
    badgeGroup.value.getNode().off('mouseup touchend')
  }
})

// --- EVENT HANDLERS ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handlePointerDown = (e: any) => {
  // CRITICAL FIX: Konva relies on event bubbling to initiate drag sequences!
  // If we cancel the bubble while draggable is true, it refuses to drag.
  if (!props.draggable) {
    e.cancelBubble = true // Prevents a "hit" from being logged underneath the fielder
  }

  pressTimer = setTimeout(() => {
    pressTimer = null
    emit('longpress') // Notify parent of long press!
  }, 400) // 400ms represents a crisp long press
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handlePointerUp = (e: any) => {
  if (!props.draggable) {
    e.cancelBubble = true
  }

  if (pressTimer) {
    clearTimeout(pressTimer)
    pressTimer = null
    emit('select') // Timer wasn't reached, it was just a short tap!
  }
}

const positionText = computed(() => {
  return props.position
    ? settingsStore.positionStrings[props.position] || String(props.position)
    : ''
})
</script>
